document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const rootPath = () => window.Shopify?.routes?.root || '/';
const announce = (message) => {
  const region = document.querySelector('#a11y-page-status');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
};

// ---------------------------------------------------------------------------
// Skeleton loading system. Every async state on the storefront shows
// shimmering placeholders: busy regions (.skeleton-live[aria-busy]) turn their
// own content into bars, async lists get skeleton rows/cards, lazy media
// shimmers until painted and busy buttons shimmer instead of spinning.
// ---------------------------------------------------------------------------
const skeletonRows = (count = 4) =>
  `<ul class="skeleton-list" role="list" aria-hidden="true">${Array.from(
    { length: count },
    () =>
      '<li class="skeleton-row"><span class="skeleton skeleton--circle"></span><span><span class="skeleton skeleton--text"></span><span class="skeleton skeleton--text"></span></span></li>',
  ).join('')}</ul>`;

const mediaSkeletonSelector =
  '.card__media, .product-media, .cart-line__media, .cart-suggest__media, .game-card__media, .product-thumbnails a';

const hydrateMediaSkeletons = (root = document) => {
  if (!root.querySelectorAll) return;
  root.querySelectorAll('img').forEach((img) => {
    const host = img.closest(mediaSkeletonSelector);
    if (!host || host.hasAttribute('data-skeleton-ready')) return;
    host.setAttribute('data-skeleton-ready', '');
    const done = () => host.classList.add('is-loaded');
    if (img.complete) {
      done();
      return;
    }
    host.classList.add('has-skeleton');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });
};

const setButtonLoading = (button, loading) => {
  if (!button) return;
  button.classList.toggle('is-loading', loading);
  button.disabled = loading;
  if (loading) button.setAttribute('aria-disabled', 'true');
  else button.removeAttribute('aria-disabled');
};

class ThemeDialog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.previewImages = [...this.dialog.querySelectorAll('[data-dialog-image]')];
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onDialogClick = this.handleDialogClick.bind(this);
    document.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', () => this.trigger?.focus());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('click', this.onDialogClick);
  }

  handleClick(event) {
    const opener = event.target.closest(`[data-dialog-open="${this.id}"]`);
    const closer = event.target.closest(`[data-dialog-close="${this.id}"]`);
    if (opener) {
      event.preventDefault();
      this.trigger = opener;
      if (this.previewImages.length && opener.dataset.imagePreviewIndex !== undefined) {
        this.setPreviewImage(Number(opener.dataset.imagePreviewIndex));
      } else {
        const dialogImage = this.dialog.querySelector('[data-dialog-image]');
        if (dialogImage && opener.dataset.imagePreviewSrc) {
          dialogImage.src = opener.dataset.imagePreviewSrc;
          dialogImage.alt = opener.dataset.imagePreviewAlt || '';
        }
      }
      // data-dialog-swap: the opener lives inside another dialog (e.g. the
      // empty cart drawer) that should give way to this one.
      if (opener.hasAttribute('data-dialog-swap')) {
        const parentDialog = opener.closest('dialog[open]');
        if (parentDialog && parentDialog !== this.dialog) {
          this.trigger = parentDialog.closest('theme-dialog')?.trigger || opener;
          parentDialog.close();
        }
      }
      if (!this.dialog.open) {
        this.dialog.showModal();
        requestAnimationFrame(() => this.dialog.querySelector('[autofocus], input, button, a')?.focus());
      }
    }
    if (closer) this.dialog.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.dialog.close();
  }

  handleDialogClick(event) {
    if (event.target === this.dialog) {
      this.dialog.close();
      return;
    }
    const control = event.target.closest('[data-image-preview-direction]');
    if (!control || !this.previewImages.length) return;
    event.preventDefault();
    this.setPreviewImage(this.previewIndex + Number(control.dataset.imagePreviewDirection));
  }

  setPreviewImage(index) {
    if (!this.previewImages.length) return;
    this.previewIndex = (index + this.previewImages.length) % this.previewImages.length;
    this.previewImages.forEach((image, imageIndex) => {
      const selected = imageIndex === this.previewIndex;
      image.hidden = !selected;
      if (selected && !image.getAttribute('src')) image.src = image.dataset.dialogImageSrc;
    });
    const current = this.dialog.querySelector('[data-image-preview-current]');
    if (current) current.textContent = String(this.previewIndex + 1);
  }

  open(trigger) {
    this.trigger = trigger || document.activeElement;
    if (!this.dialog.open) this.dialog.showModal();
  }
}
customElements.define('theme-dialog', ThemeDialog);

/* Typo-tolerant search box.
   A compact product index (handle, title, price, thumb, game) is fetched once
   per session from /collections/all?view=search-index (250 products a page,
   cached in sessionStorage) and matched client-side: prefix and substring
   matches first, then Damerau-Levenshtein / bigram similarity so "Chicle"
   still finds "Chicken", "Chicka" or "Chili". Shopify's suggest endpoint is
   the fallback while the index loads or if it cannot be fetched. */
const fuzzyNormalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const editDistance = (a, b) => {
  // Damerau-Levenshtein (optimal string alignment), capped for speed
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev2 = null;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, prev2[j - 2] + 1);
      }
      cur[j] = value;
    }
    prev2 = prev;
    prev = cur;
  }
  return prev[n];
};

const bigrams = (text) => {
  const out = new Map();
  for (let i = 0; i < text.length - 1; i += 1) {
    const key = text.slice(i, i + 2);
    out.set(key, (out.get(key) || 0) + 1);
  }
  return out;
};

const diceSimilarity = (a, b) => {
  if (a.length < 2 || b.length < 2) return 0;
  const ga = bigrams(a);
  const gb = bigrams(b);
  let shared = 0;
  ga.forEach((count, key) => {
    if (gb.has(key)) shared += Math.min(count, gb.get(key));
  });
  return (2 * shared) / (a.length - 1 + b.length - 1);
};

// similarity of a query token against one title word (typo-tolerant prefix)
const tokenSimilarity = (token, word) => {
  if (word.startsWith(token)) return 1;
  if (word.includes(token) && token.length >= 3) return 0.92;
  const head = word.slice(0, Math.min(word.length, token.length + 1));
  const distance = editDistance(token, head);
  const prefixScore = 1 - distance / Math.max(token.length, head.length);
  const wholeScore = 1 - editDistance(token, word) / Math.max(token.length, word.length);
  return Math.max(prefixScore, wholeScore);
};

const escapeHtml = (text) =>
  String(text).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
  );

const highlightTitle = (title, query, bestWord) => {
  const lower = title.toLowerCase();
  const q = query.toLowerCase();
  const index = q ? lower.indexOf(q) : -1;
  if (index >= 0) {
    return (
      escapeHtml(title.slice(0, index)) +
      '<mark>' +
      escapeHtml(title.slice(index, index + q.length)) +
      '</mark>' +
      escapeHtml(title.slice(index + q.length))
    );
  }
  if (bestWord) {
    const pattern = new RegExp('(' + bestWord.replace(/[.*+?^$()|[\]{}\\]/g, '\\$&') + ')', 'i');
    const parts = title.split(pattern);
    if (parts.length > 1) {
      return parts.map((part, i) => (i % 2 ? '<mark>' + escapeHtml(part) + '</mark>' : escapeHtml(part))).join('');
    }
  }
  return escapeHtml(title);
};

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    this.timer = null;
    this.controller = null;
    this.requestId = 0;
    this.index = null;
    this.indexPromise = null;
    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 300);
    });
    // warm the index as soon as the box gets focus so the first keystrokes are instant
    this.input.addEventListener('focus', () => this.loadIndex(), { once: true });
    this.results.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        const first = this.results.querySelector('a');
        if (first) {
          event.preventDefault();
          first.focus();
        }
      }
      // collection search has no form: Enter lists every match on the page
      if (event.key === 'Enter' && this.dataset.scope === 'collection') {
        event.preventDefault();
        window.clearTimeout(this.timer);
        this.showAll = true;
        this.search();
      }
    });
    this.results.addEventListener('click', (event) => {
      if (!event.target.closest('[data-show-all]')) return;
      this.showAll = true;
      this.search();
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.controller?.abort();
  }

  handleKeydown(event) {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const links = Array.from(this.results.querySelectorAll('li a'));
    const current = links.indexOf(document.activeElement);
    if (current < 0) return;
    event.preventDefault();
    const next = event.key === 'ArrowDown' ? links[current + 1] : links[current - 1];
    if (next) next.focus();
    else if (event.key === 'ArrowUp') this.input.focus();
  }

  get storageKey() {
    return 'bloxman-search-index:v3:' + (this.dataset.indexVersion || '0');
  }

  // collection pages only search their own products
  get scopeFilter() {
    const handle = (this.dataset.collectionHandle || '').toLowerCase();
    if (!handle || handle === 'all') return null;
    return (item) => item.collections.includes(handle);
  }

  loadIndex() {
    if (this.index) return Promise.resolve(this.index);
    if (this.indexPromise) return this.indexPromise;
    const base = this.dataset.indexUrl;
    if (!base) return Promise.reject(new Error('no index url'));
    try {
      const cached = window.sessionStorage?.getItem(this.storageKey);
      if (cached) {
        this.index = this.prepareIndex(JSON.parse(cached));
        return Promise.resolve(this.index);
      }
    } catch {
      /* storage unavailable */
    }
    const fetchPage = async (page) => {
      // no Accept: application/json here — Shopify would answer the .json route with a 404
      const response = await fetch(base + '&page=' + page, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('index page ' + page + ' failed');
      return JSON.parse(await response.text());
    };
    this.indexPromise = (async () => {
      const first = await fetchPage(1);
      const rest = [];
      for (let page = 2; page <= (first.pages || 1); page += 1) rest.push(fetchPage(page));
      const pages = [first, ...(await Promise.all(rest))];
      const items = pages.flatMap((data) => data.items || []);
      try {
        window.sessionStorage?.setItem(this.storageKey, JSON.stringify(items));
      } catch {
        /* quota exceeded: fine, fetch again next time */
      }
      this.index = this.prepareIndex(items);
      return this.index;
    })();
    this.indexPromise.catch(() => {
      this.indexPromise = null;
    });
    return this.indexPromise;
  }

  prepareIndex(items) {
    return items.map(([handle, title, price, thumb, game, gameTitle, collectionHandles]) => {
      const normalized = fuzzyNormalize(title);
      const gameNormalized = fuzzyNormalize((gameTitle || '') + ' ' + (game || ''));
      return {
        handle,
        title,
        price,
        thumb,
        game,
        normalized,
        words: normalized.split(' ').filter(Boolean),
        gameWords: gameNormalized.split(' ').filter(Boolean),
        gameCompact: gameNormalized.replace(/ /g, ''),
        collections: String(collectionHandles || '')
          .toLowerCase()
          .split(',')
          .filter(Boolean),
      };
    });
  }

  // does the query name a game (e.g. "adoptme", "murder mystery", "gag 2")?
  matchesGame(tokens, q, item) {
    if (!item.gameWords.length) return false;
    const compact = q.replace(/ /g, '');
    if (compact.length >= 3 && item.gameCompact.startsWith(compact)) return true;
    if (compact.length >= 4 && diceSimilarity(compact, item.gameCompact) >= 0.8) return true;
    return tokens.every((token) => item.gameWords.some((word) => tokenSimilarity(token, word) >= 0.8));
  }

  rank(query, limit = 8) {
    const q = fuzzyNormalize(query);
    const tokens = q.split(' ').filter(Boolean);
    if (!tokens.length) return [];
    const filter = this.scopeFilter;
    const scored = [];
    for (const item of this.index) {
      if (filter && !filter(item)) continue;
      let score;
      let bestWord = '';
      if (item.normalized === q) {
        score = 1.3;
      } else if (item.normalized.startsWith(q)) {
        score = 1.2;
      } else if (item.words.some((word) => word.startsWith(q))) {
        score = 1.15;
      } else if (item.normalized.includes(q)) {
        score = 1.1;
      } else {
        let total = 0;
        let ok = true;
        for (const token of tokens) {
          let best = 0;
          let bestCandidate = '';
          for (const word of item.words) {
            const sim = tokenSimilarity(token, word);
            if (sim > best) {
              best = sim;
              bestCandidate = word;
            }
          }
          if (tokens.length === 1) {
            // a whole-title bigram overlap rescues "adoptme" style queries
            best = Math.max(best, diceSimilarity(token, item.normalized.replace(/ /g, '')) * 0.95);
          }
          const minimum = token.length <= 3 ? 0.67 : 0.6;
          if (best < minimum) {
            ok = false;
            break;
          }
          total += best;
          if (!bestWord) bestWord = bestCandidate;
        }
        if (ok) {
          score = (total / tokens.length) * 0.98;
        } else if (this.matchesGame(tokens, q, item)) {
          score = 0.7;
        } else {
          continue;
        }
      }
      // shorter titles first among equals
      score -= Math.min(item.title.length, 60) / 4000;
      scored.push({ item, score, bestWord });
    }
    scored.sort((a, b) => b.score - a.score);
    this.lastMatchCount = scored.length;
    return scored.slice(0, limit);
  }

  renderMatches(query, matches, container = this.results, { viewAllLink = true } = {}) {
    const searchUrl = this.dataset.searchUrl || rootPath() + 'search';
    const productsUrl = this.dataset.productsUrl || rootPath() + 'products/';
    const scoped = this.dataset.scope === 'collection';
    let viewAll = '';
    if (scoped) {
      // collection pages keep the visitor on the page: expand the list instead of leaving
      const total = this.lastMatchCount || matches.length;
      if (total > matches.length) {
        viewAll =
          '<button class="button button--secondary button--full" type="button" data-show-all>' +
          escapeHtml(this.dataset.showAllLabel || 'Show all matching items') +
          ' (' +
          total +
          ')</button>';
      }
    } else if (viewAllLink) {
      viewAll =
        '<a class="button button--secondary button--full" href="' +
        escapeHtml(searchUrl + '?q=' + encodeURIComponent(query) + '&type=product') +
        '">' +
        escapeHtml(this.dataset.viewAllLabel || 'View all results') +
        '</a>';
    }
    if (!matches.length) {
      container.innerHTML =
        '<div class="predictive-results"><div class="predictive-results__empty"><span aria-hidden="true">?</span>' +
        '<div><strong>' +
        escapeHtml(this.dataset.emptyTitle || 'No item match yet') +
        '</strong><small>' +
        escapeHtml(this.dataset.emptyText || 'Try an item name or choose a game below.') +
        '</small></div></div>' +
        // Nothing matched, so the visitor is one click from leaving. Offer the
        // agent instead of only offering them somewhere else to look.
        (query
          ? '<button class="predictive-results__ask" type="button" data-reqitem-open' +
            ' data-reqitem-source="a search with no results" data-reqitem-item="' +
            escapeHtml(query) +
            '">Ask us to find it</button>'
          : '') +
        (scoped ? '' : viewAll) +
        '</div>';
      return;
    }
    const rows = matches
      .map(({ item, bestWord }) => {
        const thumb = item.thumb
          ? '<img src="' + escapeHtml(item.thumb) + '" width="48" height="48" loading="lazy" alt="">'
          : '<span class="predictive-results__thumb" aria-hidden="true"></span>';
        const meta = [item.price, item.game].filter(Boolean).map(escapeHtml).join(' · ');
        return (
          '<li><a href="' +
          escapeHtml(productsUrl + item.handle) +
          '">' +
          thumb +
          '<span><strong>' +
          highlightTitle(item.title, query, bestWord) +
          '</strong><small>' +
          meta +
          '</small></span></a></li>'
        );
      })
      .join('');
    container.innerHTML =
      '<div class="predictive-results"><h3 class="caption">' +
      escapeHtml(this.dataset.productsLabel || 'Products') +
      '</h3><ul role="list">' +
      rows +
      '</ul>' +
      viewAll +
      '</div>';
  }

  // Search results page with no server hits: list the closest typo-tolerant matches.
  async fillFallback(container) {
    const query = (container.dataset.query || '').trim();
    if (query.length < 2) return;
    try {
      const index = await this.loadIndex();
      if (!index || !index.length) return;
      const matches = this.rank(query, 12);
      if (!matches.length) return;
      const target = container.querySelector('[data-fuzzy-results]') || container;
      this.renderMatches(query, matches, target, { viewAllLink: false });
      container.hidden = false;
    } catch {
      /* index unavailable: leave the empty state as it is */
    }
  }

  async search() {
    const query = this.input.value.trim();
    // every keystroke supersedes the previous lookup; a cleared field must
    // never be overwritten by a late answer
    const requestId = (this.requestId += 1);
    if (query.length < 2) {
      this.controller?.abort();
      this.results.replaceChildren();
      this.removeAttribute('aria-busy');
      return;
    }
    this.setAttribute('aria-busy', 'true');
    if (!this.results.querySelector('.predictive-results')) this.results.innerHTML = skeletonRows(4);
    try {
      const index = await this.loadIndex();
      if (requestId !== this.requestId) return;
      if (index && index.length) {
        const limit = this.showAll ? 200 : 8;
        this.showAll = false;
        this.renderMatches(query, this.rank(query, limit));
        return;
      }
      throw new Error('empty index');
    } catch {
      if (requestId !== this.requestId) return;
      await this.searchRemote(query, requestId);
    } finally {
      if (requestId === this.requestId) this.removeAttribute('aria-busy');
    }
  }

  async searchRemote(query, requestId) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url =
      rootPath() +
      'search/suggest?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
    try {
      const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search failed: ' + response.status);
      const text = await response.text();
      if (requestId !== this.requestId) return;
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const content = parsed.querySelector('#shopify-section-predictive-search');
      const nodes = Array.from(content?.childNodes || []).map((node) => node.cloneNode(true));
      this.results.replaceChildren(...nodes);
      hydrateMediaSkeletons(this.results);
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === this.requestId) {
        this.results.textContent = this.dataset.errorMessage || 'Search is unavailable.';
      }
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

const bootFuzzyFallbacks = () => {
  const fallbacks = document.querySelectorAll('[data-fuzzy-fallback]');
  if (!fallbacks.length) return;
  const finder = document.querySelector('predictive-search');
  if (!finder || typeof finder.fillFallback !== 'function') return;
  fallbacks.forEach((container) => finder.fillFallback(container));
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootFuzzyFallbacks);
else bootFuzzyFallbacks();

const replaceSection = (id, html, selector) => {
  if (!html) return;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const source = parsed.querySelector(selector || `#${id}`);
  const target = document.querySelector(`#${id}`);
  if (source && target) target.replaceWith(source);
};

// Live section-instance ids (e.g. sections--123__cart_drawer). Requesting
// the bare type would render the section without its saved settings.
const sectionIdOf = (selector) => {
  const wrapper = document.querySelector(selector)?.closest('.shopify-section');
  return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
};
const cartSectionIds = () => {
  const ids = [sectionIdOf('#CartDrawerSection') || 'cart-drawer', sectionIdOf('#HeaderCartCount') || 'header'];
  const mainCart = sectionIdOf('#MainCartSection');
  if (mainCart) ids.push(mainCart);
  return ids;
};
const cartSectionsParam = () => cartSectionIds().join(',');

// ---------------------------------------------------------------------------
// Roblox username: a cart attribute that lands on the order. The value is
// kept in memory so drawer re-renders never lose what the buyer typed.
// ---------------------------------------------------------------------------
let robloxMemory = '';
const robloxAttribute = 'Roblox username';

// Buyers may type "player123" or "@player123"; the stored value is always
// "@player123" so the order shows the username in one recognisable format.
const normalizeRobloxUsername = (raw) => {
  const bare = String(raw || '')
    .trim()
    .replace(/^[@\s]+/, '');
  return /^[A-Za-z0-9_]{3,20}$/.test(bare) ? `@${bare}` : String(raw || '').trim();
};

const robloxUsernameError = (input) => {
  const value = input.value.trim().replace(/^@/, '');
  if (!value) return 'Enter your Roblox username to continue.';
  if (value.includes('@')) return 'Use your Roblox username, not your email address.';
  if (/\s/.test(value)) return 'Usernames cannot contain spaces. Use your username, not your display name.';
  if (!/^[A-Za-z0-9_]+$/.test(value)) return 'Use only letters, numbers and an underscore.';
  if (value.length < 3 || value.length > 20) return 'Your Roblox username must contain 3 to 20 characters.';
  return '';
};

const saveRobloxUsername = async (input) => {
  const value = normalizeRobloxUsername(input.value);
  input.value = value;
  if (!value || !input.checkValidity()) return false;
  if (input.dataset.saving === 'true') return false;
  input.dataset.saving = 'true';
  robloxMemory = value;
  try {
    const response = await fetch(`${rootPath()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [robloxAttribute]: value } }),
    });
    if (!response.ok) throw new Error('Username save failed');
    return true;
  } catch {
    return false;
  } finally {
    delete input.dataset.saving;
  }
};

const restoreRobloxUsername = (root = document) => {
  root.querySelectorAll?.('[data-cart-roblox-input]').forEach((input) => {
    const current = input.value.trim();
    if (current) robloxMemory = current;
    else if (robloxMemory) {
      input.value = robloxMemory;
      saveRobloxUsername(input);
    }
  });
};

const updateRobloxSummary = (input) => {
  const field = input.closest('[data-cart-roblox]');
  const name = field?.querySelector('[data-cart-roblox-summary-name]');
  if (name) name.textContent = normalizeRobloxUsername(input.value);
};

const markRobloxInvalid = (input, invalid) => {
  const field = input.closest('[data-cart-roblox]');
  field?.classList.toggle('is-invalid', invalid);
  const error = field?.querySelector('[data-cart-roblox-error]');
  input.setAttribute('aria-invalid', String(invalid));
  if (error) {
    error.textContent = invalid ? robloxUsernameError(input) : '';
    error.hidden = !invalid;
  }
  const hint = field?.querySelector('[data-cart-roblox-hint]');
  if (hint) {
    hint.hidden = invalid;
    hint.textContent = input.value.trim() && !robloxUsernameError(input)
      ? 'Check this matches your Roblox username. Your delivery agent will confirm it in chat.'
      : 'Enter your Roblox username, not your display name or email.';
  }
};

document.addEventListener('input', (event) => {
  const input = event.target.closest?.('[data-cart-roblox-input]');
  if (!input) return;
  const field = input.closest('[data-cart-roblox]');
  field?.classList.add('is-editing');
  field?.classList.remove('is-saved');
  input.setCustomValidity('');
  robloxMemory = input.value.trim();
  if (input.checkValidity()) {
    field?.classList.add('is-valid');
    markRobloxInvalid(input, false);
  } else {
    field?.classList.remove('is-valid');
    markRobloxInvalid(input, true);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest?.('[data-cart-roblox-input]');
  if (!input) return;
  if (document.activeElement === input) return;
  saveRobloxUsername(input).then((saved) => {
    if (saved) {
      updateRobloxSummary(input);
      input.closest('[data-cart-roblox]')?.classList.remove('is-editing');
      input.closest('[data-cart-roblox]')?.classList.add('is-saved');
      announce(input.dataset.savedMessage || 'Roblox username saved.');
    }
  });
});

// Blur is the natural save action for this compact drawer field. Keep the
// explicit change handler above for browsers that emit it before blur, but
// also save here so clicking the CTA or another control always collapses it.
document.addEventListener('blur', (event) => {
  const input = event.target.closest?.('[data-cart-roblox-input]');
  if (!input || !input.checkValidity()) return;
  saveRobloxUsername(input).then((saved) => {
    if (!saved) return;
    const field = input.closest('[data-cart-roblox]');
    updateRobloxSummary(input);
    field?.classList.remove('is-editing');
    field?.classList.add('is-saved', 'is-valid');
  });
}, true);

document.addEventListener('keydown', (event) => {
  const input = event.target.closest?.('[data-cart-roblox-input]');
  if (!input || event.key !== 'Enter') return;
  event.preventDefault();
  if (!input.checkValidity()) return;
  saveRobloxUsername(input).then((saved) => {
    if (!saved) return;
    const field = input.closest('[data-cart-roblox]');
    updateRobloxSummary(input);
    field?.classList.remove('is-editing');
    field?.classList.add('is-saved', 'is-valid');
  });
});

document.addEventListener('click', (event) => {
  const edit = event.target.closest?.('[data-cart-roblox-edit]');
  if (!edit) return;
  const field = edit.closest('[data-cart-roblox]');
  const input = field?.querySelector('[data-cart-roblox-input]');
  field?.classList.add('is-editing');
  requestAnimationFrame(() => input?.focus());
});

document.addEventListener(
  'invalid',
  (event) => {
    const input = event.target.closest?.('[data-cart-roblox-input]');
    if (!input) return;
    input.setCustomValidity(robloxUsernameError(input));
    markRobloxInvalid(input, true);
  },
  true,
);

// Checkout needs a valid username; "Update cart" on the cart page does not.
// With Stripe checkout on (Theme settings → Stripe checkout) the checkout
// button carries data-stripe-checkout: the form's attributes and note are
// saved to the cart and the Stripe page opens instead of Shopify Checkout.
// Without JavaScript the same button still posts to Shopify Checkout.
const saveCartFormFields = (form) => {
  const attributes = {};
  form.querySelectorAll('[name^="attributes["]').forEach((field) => {
    const key = field.name.slice('attributes['.length, -1).trim();
    if (key) attributes[key] = field.value.trim();
  });
  const payload = { attributes };
  const note = form.querySelector('[name="note"]');
  if (note) payload.note = note.value;
  if (!Object.keys(attributes).length && !note) return Promise.resolve();
  return fetch(`${rootPath()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
};

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('[data-cart-checkout-form]');
  if (!form) return;
  const submitter = event.submitter;
  const stripeButton = submitter?.hasAttribute?.('data-stripe-checkout') ? submitter : null;
  if (submitter && submitter.name && submitter.name !== 'checkout' && !stripeButton) return;
  const input = form.querySelector('[data-cart-roblox-input]');
  if (input) {
    input.value = normalizeRobloxUsername(input.value);
    input.setCustomValidity('');
    if (!input.checkValidity()) {
      event.preventDefault();
      input.setCustomValidity(robloxUsernameError(input));
      markRobloxInvalid(input, true);
      input.reportValidity();
      input.focus();
      return;
    }
    markRobloxInvalid(input, false);
  }
  if (!stripeButton && holdCheckoutForPaymentFee(event, form, submitter)) return;
  if (!stripeButton) return;
  event.preventDefault();
  setButtonLoading(stripeButton, true);
  const target = stripeButton.dataset.stripeCheckout || `${rootPath()}cart?view=checkout`;
  saveCartFormFields(form).finally(() => window.location.assign(target));
});

const afterCartRender = () => {
  restoreRobloxUsername();
  hydrateMediaSkeletons(document.querySelector('#CartDrawerSection') || document);
  hydrateMediaSkeletons(document.querySelector('#MainCartSection') || document);
};

const applyCartSections = (payload, options = {}) => {
  const [drawerId, headerId, mainCartId] = cartSectionIds();
  replaceSection('CartDrawerSection', payload.sections?.[drawerId], '#CartDrawerSection');
  replaceSection('HeaderCartCount', payload.sections?.[headerId], '#HeaderCartCount');
  if (mainCartId) replaceSection('MainCartSection', payload.sections?.[mainCartId], '#MainCartSection');
  afterCartRender();
  if (options.paymentFee !== false) syncPaymentFee();
};

const bumpCartCount = () => {
  const count = document.querySelector('#HeaderCartCount');
  if (!count) return;
  count.classList.remove('is-bumped');
  requestAnimationFrame(() => count.classList.add('is-bumped'));
  window.setTimeout(() => count.classList.remove('is-bumped'), 650);
};

const reopenDrawerAfter = (callback) => {
  const drawer = document.querySelector('#CartDrawer');
  const drawerWasOpen = Boolean(drawer?.dialog?.open);
  // Close before swapping the drawer: replacing an open modal dialog
  // strands the page in the inert/top-layer state in some engines.
  drawer?.dialog?.close();
  callback();
  if (drawerWasOpen) document.querySelector('#CartDrawer')?.open();
};

class ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  async submit(event) {
    if (!this.form.reportValidity()) return;
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    const error = this.querySelector('[data-product-error]');
    setButtonLoading(button, true);
    if (error) error.hidden = true;
    const data = new FormData(this.form);
    data.append('sections', cartSectionsParam());
    data.append('sections_url', window.location.pathname);
    try {
      // Checked add-ons (trade recording, insurance) go in first so the
      // sections returned by the main request already include them. Add-ons
      // already in the cart are not added twice.
      let addons = [...this.form.querySelectorAll('[data-addon-variant]:checked')].map((box) => ({
        id: Number(box.dataset.addonVariant),
        quantity: 1,
      }));
      if (addons.length) {
        try {
          const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = await cartResponse.json();
          const inCart = new Set((cart.items || []).map((item) => item.variant_id));
          addons = addons.filter((addon) => !inCart.has(addon.id));
        } catch {
          // fall through: add the add-ons anyway
        }
      }
      if (addons.length) {
        try {
          await fetch(`${rootPath()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: addons }),
          });
        } catch {
          // the main item still gets added below
        }
      }
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }],
          sections: data.get('sections'),
          sections_url: data.get('sections_url'),
        }),
      });
      // Shopify can return an HTML error page in preview mode. Read the body
      // safely so a JSON parse error does not leak into the product card.
      const responseText = await response.text();
      let payload = {};
      try {
        payload = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(response.ok ? 'The item could not be added right now.' : 'The cart service is unavailable. Please refresh and try again.');
      }
      if (!response.ok) throw new Error(payload.description || 'Item could not be added.');
      document.querySelector('#CartDrawer')?.dialog?.close();
      applyCartSections(payload);
      bumpCartCount();
      document.querySelector('#CartDrawer')?.open(button);
      announce(this.dataset.successMessage || 'Added to cart.');
    } catch (requestError) {
      if (error) {
        error.textContent = requestError.message;
        error.hidden = false;
        error.focus();
      }
    } finally {
      setButtonLoading(button, false);
    }
  }
}
customElements.define('product-form', ProductForm);

class VariantSelects extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const data = this.querySelector('script[type="application/json"]');
    try {
      this.variants = JSON.parse(data?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.select = this.querySelector('select');
    this.select?.addEventListener('change', () => this.update());
  }

  update() {
    const variant = this.variants.find((item) => String(item.id) === this.select.value);
    if (!variant) return;
    const scope = this.closest('[data-product-section]');
    const input = scope?.querySelector('input[name="id"]');
    if (input) input.value = variant.id;
    scope?.querySelector('[data-product-price]')?.replaceChildren(document.createTextNode(variant.price_formatted));
    const compare = scope?.querySelector('[data-product-compare]');
    if (compare) {
      compare.textContent = variant.compare_at_price_formatted || '';
      compare.hidden = !variant.compare_at_price_formatted;
    }
    const livePrice = scope?.querySelector('[data-live-price]');
    if (livePrice && variant.price != null) {
      livePrice.dataset.unitPrice = String(variant.price);
      livePrice.dataset.unitCompare = String(variant.compare_at_price || 0);
      if (typeof refreshLivePrice === 'function') refreshLivePrice(scope);
    }
    const button = scope?.querySelector('[data-add-to-cart]');
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
    }
    const sku = scope?.querySelector('[data-product-sku]');
    if (sku) sku.textContent = variant.sku || '—';
    const availability = scope?.querySelector('[data-product-availability]');
    if (availability)
      availability.textContent = variant.available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
    if (variant.featured_media_id) {
      scope
        ?.querySelector(`[data-media-id="${variant.featured_media_id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
    if (history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
  }
}
customElements.define('variant-selects', VariantSelects);

class CartItems extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (input) this.change(input.dataset.line, input.value);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.change(remove.dataset.line, 0);
    });
  }

  async change(line, quantity) {
    if (this.hasAttribute('aria-busy')) return;
    this.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-cart-error]');
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${rootPath()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: cartSectionsParam(),
          sections_url: location.pathname,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Cart could not be updated.');
      reopenDrawerAfter(() => applyCartSections(payload));
      bumpCartCount();
      announce(this.dataset.updatedMessage || 'Cart updated.');
    } catch (requestError) {
      if (error) {
        error.textContent = requestError.message;
        error.hidden = false;
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}
customElements.define('cart-items', CartItems);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.initialized || !this.dataset.url) return;
    this.initialized = true;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '300px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  async load() {
    this.controller = new AbortController();
    try {
      const response = await fetch(this.dataset.url, {
        signal: this.controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = parsed.querySelector('product-recommendations');
      if (!source?.querySelector('.product-card')) {
        this.hidden = true;
        return;
      }
      const nodes = Array.from(source.childNodes).map((node) => node.cloneNode(true));
      this.replaceChildren(...nodes);
      this.removeAttribute('aria-busy');
      hydrateMediaSkeletons(this);
      prepareInteractiveMotion(this);
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

let collectionPopstateInitialized = false;
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply(new FormData(this.form));
    });
    this.form.addEventListener('change', (event) => {
      if (event.target.matches('select, input[type="checkbox"]')) this.apply(new FormData(this.form));
    });
    if (!collectionPopstateInitialized) {
      collectionPopstateInitialized = true;
      window.addEventListener('popstate', () =>
        document.querySelector('collection-filters')?.load(window.location.href, false),
      );
    }
  }

  apply(formData) {
    const query = new URLSearchParams(formData).toString();
    this.load(`${window.location.pathname}${query ? `?${query}` : ''}`, true);
  }

  async load(url, updateHistory) {
    const sectionId = this.dataset.sectionId;
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    document.querySelectorAll('collection-filters').forEach((element) => element.setAttribute('aria-busy', 'true'));
    document.querySelector('.collection-results')?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(requestUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Filters failed: ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = parsed.querySelector(`#CollectionSection-${sectionId}`);
      const target = document.querySelector(`#CollectionSection-${sectionId}`);
      if (source && target) {
        target.replaceWith(source);
        if (updateHistory) history.pushState({}, '', url);
        const next = document.querySelector(`#CollectionSection-${sectionId}`);
        next?.scrollIntoView({ block: 'start' });
        hydrateMediaSkeletons(next || document);
      }
    } catch {
      this.form.submit();
    }
  }
}
customElements.define('collection-filters', CollectionFilters);

class LocalizationForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.querySelectorAll('select').forEach((select) =>
      select.addEventListener('change', () => this.querySelector('form')?.submit()),
    );
  }
}
customElements.define('localization-form', LocalizationForm);

document.addEventListener('click', (event) => {
  const control = event.target.closest('[data-quantity-action]');
  if (!control) return;
  const input = control.parentElement.querySelector('input[type="number"]');
  if (!input) return;
  const step = control.dataset.quantityAction === 'increase' ? 1 : -1;
  input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* Live product price: unit price × quantity + ticked add-ons, recomputed on
   every quantity or add-on change (no reload). The container carries the
   variant's unit/compare prices in cents and the shop money format. */
const formatMoney = (cents, format) => {
  const value = Number(cents) || 0;
  const fixed = (Math.round(value) / 100).toFixed(2);
  const [whole, decimals] = fixed.split('.');
  const group = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const pattern = format || '{{amount}}';
  return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(',');
      case 'amount_with_comma_separator':
        return group('.') + ',' + decimals;
      case 'amount_no_decimals_with_comma_separator':
        return group('.');
      case 'amount_with_apostrophe_separator':
        return group("'") + '.' + decimals;
      case 'amount_with_space_separator':
        return group(' ') + ',' + decimals;
      case 'amount_no_decimals_with_space_separator':
        return group(' ');
      case 'amount_with_period_and_space_separator':
        return group(' ') + '.' + decimals;
      default:
        return group(',') + '.' + decimals;
    }
  });
};

const refreshLivePrice = (scope) => {
  const price = scope?.querySelector('[data-live-price]');
  if (!price) return;
  const unit = Number(price.dataset.unitPrice) || 0;
  const unitCompare = Number(price.dataset.unitCompare) || 0;
  const quantityInput = scope.querySelector('input[name="quantity"]');
  const quantity = Math.max(1, Number(quantityInput?.value) || 1);
  let addons = 0;
  scope.querySelectorAll('[data-addon-variant]:checked').forEach((box) => {
    addons += Number(box.dataset.addonPrice) || 0;
  });
  const total = unit * quantity + addons;
  const current = price.querySelector('[data-product-price]');
  if (current) { current.dataset.localMoney = String(total); current.textContent = formatMoney(total, price.dataset.moneyFormat); }
  const compare = price.querySelector('[data-product-compare]');
  if (compare) {
    if (unitCompare > unit) {
      compare.textContent = formatMoney(unitCompare * quantity + addons, price.dataset.moneyFormat);
      compare.dataset.localMoney = String(unitCompare * quantity + addons);
      compare.hidden = false;
    } else {
      compare.textContent = '';
      compare.hidden = true;
    }
  }
  const saving = scope.querySelector('[data-product-saving]');
  if (saving) {
    const saved = unitCompare > unit ? (unitCompare - unit) * quantity : 0;
    saving.hidden = saved <= 0;
    const amount = saving.querySelector('[data-product-saving-amount]');
    if (amount) amount.textContent = formatMoney(saved, price.dataset.moneyFormat);
  }
};

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('input[name="quantity"], [data-addon-variant]')) return;
  refreshLivePrice(target.closest('[data-product-section]'));
});
document.addEventListener('input', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.matches('input[name="quantity"]')) {
    refreshLivePrice(target.closest('[data-product-section]'));
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape')
    document.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
});

const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionItemSelector = [
  '.section-heading',
  '.trust-item',
  '.game-route__stop',
  '.product-game-tabs__item',
  '.product-grid > *',
  '.benefit-card',
  '.step-card',
  '.safety-mini-card',
  '.safety-guarantee__panel',
  '.faq-section__items > *',
  '.faq-help',
  '.tutorial-card',
  '.reviews__grid > *',
  '.tutorial-starter-card',
  '.support-category',
  '.support-faq__items > *',
  '.claim-order__visual',
  '.claim-order__card',
  '.cart-empty-board__game',
].join(',');

let revealObserver;

const motionEnabled = () =>
  document.body.classList.contains('motion-normal') && !motionPreference.matches && 'IntersectionObserver' in window;

const revealAllMotion = () => {
  revealObserver?.disconnect();
  document.documentElement.classList.remove('motion-ready');
  document.querySelectorAll('[data-motion-reveal]').forEach((section) => section.classList.add('is-visible'));
};

const prepareMotion = (root = document) => {
  if (!motionEnabled()) {
    revealAllMotion();
    return;
  }

  revealObserver ||= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );

  const sections = root.matches?.('#MainContent .shopify-section')
    ? [root]
    : Array.from(root.querySelectorAll?.('#MainContent .shopify-section') || []);

  sections.forEach((section) => {
    if (section.hasAttribute('data-motion-reveal')) return;
    section.setAttribute('data-motion-reveal', '');

    const motionItems = Array.from(section.querySelectorAll(motionItemSelector));
    motionItems.forEach((item, index) => {
      item.setAttribute('data-motion-item', '');
      item.style.setProperty('--motion-index', Math.min(index, 7));
    });

    const bounds = section.getBoundingClientRect();
    if (bounds.top < window.innerHeight * 0.94 && bounds.bottom > 0) section.classList.add('is-visible');
    else revealObserver.observe(section);
  });

  document.documentElement.classList.add('motion-ready');
};

const prepareInteractiveMotion = (root = document) => {
  if (!motionEnabled() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const hero = root.matches?.('.hero') ? root : root.querySelector?.('.hero');
  if (hero && !hero.hasAttribute('data-motion-parallax')) {
    const orbit = hero.querySelector('.game-orbit');
    hero.setAttribute('data-motion-parallax', '');
    hero.addEventListener('pointermove', (event) => {
      const bounds = hero.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
      hero.style.setProperty('--hero-pointer-x', `${Math.round(x * 100)}%`);
      hero.style.setProperty('--hero-pointer-y', `${Math.round(y * 100)}%`);
      orbit?.style.setProperty('--orbit-tilt-x', `${((0.5 - y) * 3.4).toFixed(2)}deg`);
      orbit?.style.setProperty('--orbit-tilt-y', `${((x - 0.5) * 4.4).toFixed(2)}deg`);
    });
    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--hero-pointer-x', '78%');
      hero.style.setProperty('--hero-pointer-y', '34%');
      orbit?.style.setProperty('--orbit-tilt-x', '0deg');
      orbit?.style.setProperty('--orbit-tilt-y', '0deg');
    });
  }

  root.querySelectorAll?.('.product-card, a.game-card').forEach((card) => {
    if (card.hasAttribute('data-motion-tilt')) return;
    card.setAttribute('data-motion-tilt', '');
    card.addEventListener('pointermove', (event) => {
      const bounds = card.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
      card.style.setProperty('--card-pointer-x', `${Math.round(x * 100)}%`);
      card.style.setProperty('--card-pointer-y', `${Math.round(y * 100)}%`);
      card.style.setProperty('--card-tilt-x', `${((0.5 - y) * 4.2).toFixed(2)}deg`);
      card.style.setProperty('--card-tilt-y', `${((x - 0.5) * 4.8).toFixed(2)}deg`);
    });
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--card-pointer-x', '50%');
      card.style.setProperty('--card-pointer-y', '42%');
      card.style.setProperty('--card-tilt-x', '0deg');
      card.style.setProperty('--card-tilt-y', '0deg');
    });
  });
};

const createButtonRipple = (event) => {
  if (!motionEnabled() || event.button !== 0) return;
  const button = event.target.closest('.button:not(.skip-link)');
  if (!button || button.matches(':disabled, [aria-disabled="true"]')) return;
  const bounds = button.getBoundingClientRect();
  const size = Math.max(bounds.width, bounds.height) * 1.4;
  const ripple = document.createElement('span');
  ripple.className = 'bloxman-ripple';
  ripple.setAttribute('aria-hidden', 'true');
  ripple.style.setProperty('--ripple-size', `${size}px`);
  ripple.style.left = `${event.clientX - bounds.left - size / 2}px`;
  ripple.style.top = `${event.clientY - bounds.top - size / 2}px`;
  button.append(ripple);
  ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
};

let headerMotionFrame;
let lastHeaderScrollY = window.scrollY;
const syncHeaderMotion = () => {
  if (headerMotionFrame) return;
  headerMotionFrame = requestAnimationFrame(() => {
    const header = document.querySelector('.site-header');
    if (header) {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 18);
      const menuOpen = header.querySelector('.nav-dropdown[open]');
      if (y <= header.offsetHeight * 2 || y < lastHeaderScrollY - 4 || menuOpen) {
        header.classList.remove('is-hidden');
      } else if (y > lastHeaderScrollY + 4) {
        header.classList.add('is-hidden');
      }
      lastHeaderScrollY = y;
    }
    headerMotionFrame = null;
  });
};

const animateStatValue = (element) => {
  const raw = String(element.dataset.countTo || '').replace(',', '.');
  const target = Number.parseFloat(raw);
  if (!Number.isFinite(target)) return;
  const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
  const duration = 1400;
  const start = performance.now();
  const step = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    element.textContent = (target * eased).toFixed(decimals);
    if (progress < 1) requestAnimationFrame(step);
    else {
      element.textContent = raw.includes('.') ? raw : target.toLocaleString('en-US');
      element.classList.add('is-counted');
    }
  };
  requestAnimationFrame(step);
};

const prepareStatCounters = (root = document) => {
  const values = Array.from(root.querySelectorAll?.('.stat-value[data-count-to]') || []);
  if (!values.length) return;
  if (!motionEnabled()) {
    values.forEach((element) => {
      const raw = String(element.dataset.countTo || '');
      element.textContent = raw.includes('.') ? raw : Number(raw).toLocaleString('en-US');
    });
    return;
  }
  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        counterObserver.unobserve(entry.target);
        animateStatValue(entry.target);
      });
    },
    { threshold: 0.4 },
  );
  values.forEach((element) => {
    if (element.hasAttribute('data-count-ready')) return;
    element.setAttribute('data-count-ready', '');
    element.textContent = '0';
    counterObserver.observe(element);
  });
};

// "X people viewing right now" drifts gently within the configured range.
const prepareProductPulse = (root = document) => {
  root.querySelectorAll?.('[data-product-pulse]').forEach((pulse) => {
    if (pulse.hasAttribute('data-pulse-ready')) return;
    pulse.setAttribute('data-pulse-ready', '');
    const value = pulse.querySelector('[data-pulse-watching]');
    if (!value) return;
    const min = Math.max(1, Number(pulse.dataset.min) || 1);
    const max = Math.max(min, Number(pulse.dataset.max) || min);
    const tick = () => {
      const current = Number(value.textContent) || min;
      const delta = Math.round((Math.random() - 0.5) * 3);
      const next = Math.min(max, Math.max(min, current + delta));
      if (next !== current) {
        value.textContent = String(next);
        value.classList.remove('is-bumped');
        void value.offsetWidth;
        value.classList.add('is-bumped');
      }
      window.setTimeout(tick, 7000 + Math.random() * 9000);
    };
    window.setTimeout(tick, 4000 + Math.random() * 5000);
  });
};

const initializeGoBloxMotion = () => {
  prepareMotion();
  prepareInteractiveMotion();
  prepareStatCounters();
  prepareProductPulse();
  hydrateMediaSkeletons();
  restoreRobloxUsername();
  syncHeaderMotion();
  window.addEventListener('scroll', syncHeaderMotion, { passive: true });
  document.addEventListener('pointerdown', createButtonRipple);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeGoBloxMotion);
else initializeGoBloxMotion();

motionPreference.addEventListener('change', () => {
  if (motionPreference.matches) revealAllMotion();
  else {
    prepareMotion();
    prepareInteractiveMotion();
  }
});

document.addEventListener('shopify:section:load', (event) => {
  prepareMotion(event.target);
  prepareInteractiveMotion(event.target);
  prepareStatCounters(event.target);
  prepareProductPulse(event.target);
  hydrateMediaSkeletons(event.target);
  restoreRobloxUsername(event.target);
});

document.addEventListener('click', (event) => {
  document.querySelectorAll('details.sort-menu[open]').forEach((menu) => {
    if (!menu.contains(event.target)) menu.removeAttribute('open');
  });
});

// Frequently-bought-together: one click adds both items.
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-fbt-main]');
  if (!button) return;
  setButtonLoading(button, true);
  try {
    const response = await fetch(`${rootPath()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          { id: Number(button.dataset.fbtMain), quantity: 1 },
          { id: Number(button.dataset.fbtPair), quantity: 1 },
        ],
        sections: cartSectionsParam(),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || 'Bundle could not be added.');
    document.querySelector('#CartDrawer')?.dialog?.close();
    applyCartSections(payload);
    bumpCartCount();
    document.querySelector('#CartDrawer')?.open(button);
    announce('Bundle added to cart.');
  } catch {
    window.location.assign(`${rootPath()}cart`);
  } finally {
    setButtonLoading(button, false);
  }
});

// One-click add from the cart drawer (impulse rows, empty-cart suggestions).
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-impulse-add]');
  if (!button) return;
  button.disabled = true;
  button.classList.add('is-loading');
  try {
    const response = await fetch(`${rootPath()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(button.dataset.impulseAdd), quantity: Number(button.dataset.impulseQty) || 1 }],
        sections: cartSectionsParam(),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || 'Item could not be added.');
    reopenDrawerAfter(() => applyCartSections(payload));
    bumpCartCount();
    announce('Added to cart.');
  } catch {
    button.disabled = false;
    button.classList.remove('is-loading');
  }
});

// "Record my trade" row: the "What's included" toggle reveals the copy.
document.addEventListener('click', (event) => {
  const more = event.target.closest?.('[data-trade-proof-more]');
  if (!more) return;
  const wrapper = more.closest('[data-trade-proof]');
  const details = wrapper?.querySelector('.trade-proof__details');
  if (!details) return;
  const open = details.hidden;
  details.hidden = !open;
  more.setAttribute('aria-expanded', String(open));
  wrapper.classList.toggle('is-open', open);
});

// Generic add-on rows (Delivery Insurance etc.): "What's included" reveals
// the add-on product's description.
document.addEventListener('click', (event) => {
  const more = event.target.closest?.('[data-addon-more]');
  if (!more) return;
  const details = more.closest('[data-addon-row]')?.querySelector('.addon-row__details');
  if (!details) return;
  const open = details.hidden;
  details.hidden = !open;
  more.setAttribute('aria-expanded', String(open));
});

// "Record my trade" row in the cart drawer / cart page: ticking the box adds
// the add-on line right away, unticking removes it; the cart sections are
// re-rendered from the response like every other cart change.
document.addEventListener('change', async (event) => {
  const toggle = event.target.closest?.('[data-trade-proof-cart], [data-cart-addon]');
  if (!toggle) return;
  const wrapper = toggle.closest('[data-trade-proof], [data-cart-addon-row]');
  const variantId = Number(toggle.dataset.tradeProofCart || toggle.dataset.cartAddon);
  const adding = toggle.checked;
  wrapper?.classList.add('is-loading');
  toggle.disabled = true;
  try {
    const response = await fetch(`${rootPath()}cart/${adding ? 'add' : 'change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...(adding ? { items: [{ id: variantId, quantity: 1 }] } : { id: String(variantId), quantity: 0 }),
        sections: cartSectionsParam(),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || 'Cart could not be updated.');
    reopenDrawerAfter(() => applyCartSections(payload));
    bumpCartCount();
    announce(adding ? 'Add-on added to your cart.' : 'Add-on removed from your cart.');
  } catch {
    toggle.checked = !adding;
    toggle.disabled = false;
    wrapper?.classList.remove('is-loading');
  }
});

// Trade proof player page (templates/page.trade-proof.json): plays the
// recording linked from the Order status page. Only Shopify CDN sources
// are accepted so the page cannot be pointed at arbitrary media.
const initTradeProofPlayer = () => {
  const player = document.querySelector('[data-trade-player]');
  if (!player) return;
  const params = new URLSearchParams(window.location.search);
  const order = (params.get('order') || '').replace(/[^\w#-]/g, '').slice(0, 24);
  let source = null;
  try {
    const candidate = new URL(params.get('video') || '', window.location.origin);
    const sameOriginCdn = candidate.origin === window.location.origin && candidate.pathname.startsWith('/cdn/');
    if (candidate.protocol === 'https:' && (candidate.hostname === 'cdn.shopify.com' || sameOriginCdn))
      source = candidate;
  } catch {
    source = null;
  }
  if (!source) return;

  const video = player.querySelector('[data-trade-video]');
  const empty = player.querySelector('[data-trade-empty]');
  const actions = player.querySelector('[data-trade-actions]');
  const orderLabel = player.querySelector('[data-trade-order]');
  const feedback = player.querySelector('[data-trade-feedback]');
  const download = player.querySelector('[data-trade-download]');
  const copy = player.querySelector('[data-trade-copy]');
  const share = player.querySelector('[data-trade-share]');
  const say = (message) => {
    if (feedback) feedback.textContent = message;
  };

  if (video) {
    video.src = source.href;
    video.hidden = false;
  }
  if (empty) empty.hidden = true;
  if (actions) actions.hidden = false;
  if (orderLabel && order) {
    orderLabel.textContent = `Order ${order}`;
    orderLabel.hidden = false;
  }

  const fileName = `bloxman-trade-proof${order ? `-${order.replace('#', '')}` : ''}.mp4`;
  download?.setAttribute('href', source.href);
  download?.setAttribute('download', fileName);
  download?.addEventListener('click', async (event) => {
    event.preventDefault();
    setButtonLoading(download, true);
    try {
      const response = await fetch(source.href, { mode: 'cors' });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      say('Download started.');
    } catch {
      window.open(source.href, '_blank', 'noopener');
    } finally {
      setButtonLoading(download, false);
    }
  });

  copy?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      say('Link copied — share it anywhere.');
    } catch {
      say(window.location.href);
    }
  });

  if (share && navigator.share) {
    share.hidden = false;
    share.addEventListener('click', () => {
      navigator.share({ title: document.title, url: window.location.href }).catch(() => {});
    });
  }
};
initTradeProofPlayer();

// Mobile sticky buy bar appears once the real buy row scrolls away.
const initProductStickyBar = () => {
  const bar = document.querySelector('[data-sticky-buy-bar]');
  const buyRow = document.querySelector('[data-product-section] .product-buy-row');
  const form = document.querySelector('[data-product-section] .product-form');
  if (!bar || !buyRow || !form) return;
  bar.hidden = false;
  const syncChatClearance = () => {
    document.body.style.setProperty('--product-buy-bar-height', `${bar.getBoundingClientRect().height}px`);
  };
  new ResizeObserver(syncChatClearance).observe(bar);
  new IntersectionObserver(([entry]) => {
    bar.classList.toggle('is-visible', !entry.isIntersecting);
    document.body.classList.toggle('product-buy-bar-visible', !entry.isIntersecting);
    syncChatClearance();
  }).observe(buyRow);
  bar.querySelector('[data-sticky-buy]')?.addEventListener('click', () => form.requestSubmit());
};
initProductStickyBar();

// Purchase highlights are configured in the theme editor. Keep their relative
// time labels fresh while the page is open without implying a live order feed.
const initPurchaseTickerTimes = () => {
  const labels = document.querySelectorAll('[data-purchase-time]');
  if (!labels.length) return;
  const startedAt = Date.now();
  const render = () => {
    const elapsedMinutes = Math.floor((Date.now() - startedAt) / 60000);
    labels.forEach((label) => {
      const minutes = Number(label.dataset.purchaseOffset || 0) + elapsedMinutes;
      label.textContent = minutes < 1 ? 'just now' : `${minutes} min ago`;
    });
  };
  render();
  window.setInterval(render, 30000);
};
initPurchaseTickerTimes();

// Bundle presets (Buy more & save) drive the quantity field.
document.addEventListener('change', (event) => {
  const offer = event.target.closest('[data-bundle-qty]');
  if (!offer) return;
  const quantity = offer.closest('form')?.querySelector('input[name="quantity"]');
  if (!quantity) return;
  quantity.value = offer.dataset.bundleQty;
  quantity.dispatchEvent(new Event('change', { bubbles: true }));
});

// On pointer devices the Select Game menu opens on hover; the small close
// delay lets the cursor cross the gap between the button and the list.
if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.querySelectorAll('details.blox-games-menu').forEach((menu) => {
    let closeTimer;
    menu.addEventListener('pointerenter', () => {
      window.clearTimeout(closeTimer);
      menu.setAttribute('open', '');
    });
    menu.addEventListener('pointerleave', () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => menu.removeAttribute('open'), 160);
    });
  });
}

// Collection filters, sorting and pagination swap in place instead of reloading.
const collectionAjaxLinks =
  '.filter-chip[href], .sort-menu a[href], .collection-filter-clear[href], .pagination a[href]';
let collectionFetchController;

const swapCollection = async (url, pushHistory) => {
  const root = document.querySelector('.collection-hub');
  if (!root) return;
  root.querySelector('.collection-results')?.setAttribute('aria-busy', 'true');
  collectionFetchController?.abort();
  collectionFetchController = new AbortController();
  try {
    const response = await fetch(url, { signal: collectionFetchController.signal });
    if (!response.ok) throw new Error(`Collection request failed (${response.status})`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = doc.querySelector('.collection-hub');
    if (!next) throw new Error('Collection markup missing in response');
    root.replaceWith(next);
    if (pushHistory) history.pushState({ bloxmanCollection: true }, '', url);
    document.title = doc.title;
    if (next.getBoundingClientRect().top < 0) next.scrollIntoView({ block: 'start' });
    hydrateMediaSkeletons(next);
    prepareInteractiveMotion(next);
  } catch (error) {
    if (error.name === 'AbortError') return;
    window.location.assign(url);
  }
};

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest('a[href]');
  if (!link || !link.closest('.collection-hub') || !link.matches(collectionAjaxLinks)) return;
  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return;
  event.preventDefault();
  link.closest('details.sort-menu')?.removeAttribute('open');
  swapCollection(url.pathname + url.search, true);
});

window.addEventListener('popstate', () => {
  if (!document.querySelector('.collection-hub')) return;
  swapCollection(window.location.pathname + window.location.search, false);
});

// ---------------------------------------------------------------------------
// Stripe fee note. The checkout backend owns the fee rules (service fee passed
// on to the buyer + Stripe's card minimum, GET /api/checkout/config); the cart
// drawer and cart page show the estimated fee line before the buyer reaches
// the payment page, and the exact line comes with the payment session.
// ---------------------------------------------------------------------------
(() => {
  const CACHE_KEY = 'bloxman-stripe-config';
  const CACHE_TTL = 5 * 60 * 1000;
  let configPromise = null;

  const loadFeeConfig = (backend) => {
    if (configPromise) return configPromise;
    configPromise = (async () => {
      try {
        const cached = JSON.parse(window.sessionStorage?.getItem(CACHE_KEY) || 'null');
        if (cached && cached.backend === backend && Date.now() - cached.at < CACHE_TTL) return cached.config;
      } catch {
        /* storage unavailable */
      }
      const response = await fetch(`${backend}/api/checkout/config`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('fee config unavailable');
      const config = await response.json();
      try {
        window.sessionStorage?.setItem(CACHE_KEY, JSON.stringify({ backend, at: Date.now(), config }));
      } catch {
        /* storage unavailable */
      }
      return config;
    })().catch((error) => {
      configPromise = null;
      throw error;
    });
    return configPromise;
  };

  // Mirrors feeRateFor() + computeFees() in apps/stripe-checkout/src/checkout/session.js.
  const feeRateFor = (country, fee) => {
    const domestic = (fee.domestic_countries || ['US']).map((code) => String(code).toUpperCase());
    const code = String(country || '').toUpperCase();
    const isDomestic = Boolean(code) && domestic.includes(code);
    const percent = isDomestic ? fee.percent : (fee.intl_percent ?? fee.percent);
    return {
      percent: Math.min(Math.max(Number(percent) || 0, 0), 30),
      fixed: Math.max(0, Number(fee.fixed) || 0),
    };
  };

  const estimateFees = (subtotal, config, country) => {
    if (!(subtotal > 0)) return { processing: 0, topUp: 0, total: 0 };
    const fee = config.processing_fee || {};
    let processing = 0;
    if (fee.mode && fee.mode !== 'off') {
      const rate = feeRateFor(country, fee);
      const percent = rate.percent / 100;
      const raw =
        fee.mode === 'grossup' ? (subtotal + rate.fixed) / (1 - percent) - subtotal : subtotal * percent + rate.fixed;
      processing = Math.max(0, Math.round(raw + 1e-9));
    }
    const minimum = Number(config.minimum) || 0;
    const topUp = minimum > 0 && subtotal + processing < minimum ? minimum - subtotal - processing : 0;
    return { processing, topUp, total: processing + topUp };
  };

  const formatFee = (minor, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(
        minor / 100,
      );
    } catch {
      return `${(minor / 100).toFixed(2)} ${currency}`;
    }
  };

  const fillFeeNotes = () => {
    document.querySelectorAll('[data-stripe-fee-note]:not([data-fee-ready])').forEach((note) => {
      note.setAttribute('data-fee-ready', '');
      const backend = String(note.dataset.backend || '').replace(/\/+$/, '');
      if (!backend) return;
      loadFeeConfig(backend)
        .then((config) => {
          const fees = estimateFees(Number(note.dataset.subtotal) || 0, config, note.dataset.buyerCountry);
          // Top-up only: the Liquid small-order note already says it.
          if (fees.processing <= 0) return;
          const currency = note.dataset.currency || config.currency || 'EUR';
          const feeValue = formatFee(fees.total, currency);
          const subtotal = Number(note.dataset.subtotal) || 0;
          const totalValue = formatFee(subtotal + fees.total, currency);
          note.textContent = String(note.dataset.template || '__FEE__').replace(
            '__FEE__',
            feeValue,
          );
          note.hidden = false;
          const scope = note.closest('form, .cart-summary') || document;
          scope.querySelectorAll('[data-stripe-fee-row]').forEach((row) => {
            row.hidden = false;
          });
          scope.querySelectorAll('[data-stripe-fee-value]').forEach((value) => {
            value.dataset.localMoney = String(fees.total);
            value.textContent = feeValue;
          });
          scope.querySelectorAll('[data-stripe-grand-total]').forEach((row) => {
            row.hidden = false;
          });
          scope.querySelectorAll('[data-stripe-total-value]').forEach((value) => {
            value.dataset.localMoney = String(subtotal + fees.total);
            value.textContent = totalValue;
          });
          scope.querySelectorAll('[data-stripe-min-note]').forEach((old) => {
            old.hidden = true;
          });
        })
        .catch(() => {
          /* backend unreachable: the checkout page still shows the exact line */
        });
    });
  };

  fillFeeNotes();
  new MutationObserver(fillFeeNotes).observe(document.documentElement, { childList: true, subtree: true });
})();

// ---------------------------------------------------------------------------
// Payment processing fee (Theme settings → Payment processing fee).
// A non-Plus Shopify checkout cannot price a cart line on the fly, so the
// hidden "Payment Processing Fees" product carries a price ladder — one variant
// per amount: $0.01–$10 by the cent, $10.10–$100 by 10 cents, $101–$248 by the
// dollar (assets/payment-fee-variants.json lists the ids in that order; both
// come from scripts/payment-fee-product.mjs). After every cart change the cart
// holds exactly the variant for
//   fee = (items + fixed) / (1 − (Stripe % + Shopify %)) − items
// so the store keeps the item total after Stripe's and Shopify's cut. Counts
// skip the fee line and the summaries print it as its own row. The fee is off
// while "Pay with Stripe" is on (that checkout adds its own fee) and then only
// removes leftover fee lines.
//
// Minimum quantity (same settings group): an item whose unit price is below the
// minimum line total (50¢, Stripe's smallest charge) is sold in the quantity
// that reaches it — ceil(50 / price): 10¢ → 5, 12¢ → 5, 49¢ → 2. Product pages
// start at that quantity, quantity controls refuse to go lower with an inline
// message, and cart lines that arrive below it (quick add, links) are raised.
// Add-ons and the fee line are exempt.
// ---------------------------------------------------------------------------
const PAYMENT_FEE_TOP_STEP = 24800;

const paymentFeeConfig = () => {
  try {
    return JSON.parse(document.querySelector('[data-payment-fee-config]')?.textContent || 'null');
  } catch {
    return null;
  }
};

// Position of the smallest ladder price >= cents (cents <= the top step).
const paymentFeeStep = (cents) => {
  if (cents <= 1000) return { index: cents - 1, value: cents };
  if (cents <= 10000) {
    const value = Math.ceil(cents / 10) * 10;
    return { index: 1000 + (value - 1010) / 10, value };
  }
  const value = Math.ceil(cents / 100) * 100;
  return { index: 1900 + (value - 10100) / 100, value };
};

const paymentFeeCents = (subtotal, config) => {
  if (!(subtotal > 0)) return 0;
  const country = String(config.country || '').toUpperCase();
  const domestic = String(config.domestic || 'US')
    .toUpperCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  const stripePercent = Number(domestic.includes(country) ? config.percent : config.intlPercent) || 0;
  const percent = Math.min(Math.max(stripePercent + (Number(config.shopifyPercent) || 0), 0), 30) / 100;
  const fixed = Math.max(0, Math.round(Number(config.fixed) || 0));
  return Math.max(0, Math.round((subtotal + fixed) / (1 - percent) - subtotal + 1e-9));
};

// variant id → quantity; fees above the top step repeat the top variant.
const paymentFeeLines = (cents, ids) => {
  const lines = new Map();
  if (!(cents > 0) || ids.length !== 2048) return lines;
  let top = Math.floor(cents / PAYMENT_FEE_TOP_STEP);
  const rest = cents - top * PAYMENT_FEE_TOP_STEP;
  if (rest > 0) {
    const step = paymentFeeStep(rest);
    if (step.value === PAYMENT_FEE_TOP_STEP) top += 1;
    else lines.set(ids[step.index], 1);
  }
  if (top > 0) lines.set(ids[ids.length - 1], top);
  return lines;
};

let paymentFeeMap = null;
const loadPaymentFeeMap = async (url) => {
  if (!url) return null;
  if (paymentFeeMap?.url === url) return paymentFeeMap;
  const storageKey = 'payment-fee-map';
  try {
    const cached = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (cached?.url === url && cached.ids?.length === 2048) return (paymentFeeMap = cached);
  } catch {
    // storage unavailable
  }
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Payment fee map answered ${response.status}`);
  const data = await response.json();
  paymentFeeMap = { url, handle: data.handle, ids: data.ids || [] };
  try {
    localStorage.setItem(storageKey, JSON.stringify(paymentFeeMap));
  } catch {
    // quota or private mode
  }
  return paymentFeeMap;
};

// Smallest quantity whose line total reaches the minimum (1 when one item already does).
const minQtyFor = (unitCents, config = paymentFeeConfig()) => {
  if (!config?.minQty) return 1;
  const line = Math.max(0, Math.round(Number(config.minLineCents) || 0));
  const price = Math.round(Number(unitCents) || 0);
  return price > 0 && price < line ? Math.ceil(line / price) : 1;
};

// Re-render after a rule changed the cart. An open drawer keeps its dialog —
// replacing it would replay the slide-in animation — and only its title and
// body are swapped; everything else re-renders as usual.
const applyPaymentFeeSections = (payload) => {
  const drawer = document.querySelector('#CartDrawer');
  if (!drawer?.dialog?.open) {
    applyCartSections(payload, { paymentFee: false });
    return;
  }
  const [drawerId, headerId, mainCartId] = cartSectionIds();
  const html = payload.sections?.[drawerId];
  if (html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    ['#CartDrawerTitle', '.cart-drawer__body'].forEach((selector) => {
      const current = drawer.querySelector(selector);
      const next = fresh.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    });
    const config = document.querySelector('#CartDrawerSection [data-payment-fee-config]');
    const nextConfig = fresh.querySelector('[data-payment-fee-config]');
    if (config && nextConfig) config.textContent = nextConfig.textContent;
  }
  replaceSection('HeaderCartCount', payload.sections?.[headerId], '#HeaderCartCount');
  if (mainCartId) replaceSection('MainCartSection', payload.sections?.[mainCartId], '#MainCartSection');
  afterCartRender();
};

const postCartUpdate = async (updates) => {
  const response = await fetch(`${rootPath()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections: cartSectionsParam(), sections_url: window.location.pathname }),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.description || 'Cart could not be updated.');
  return payload;
};

const reconcilePaymentFee = async () => {
  const config = paymentFeeConfig();
  if (!config) return;
  const handle = config.handle || 'payment-processing-fees';
  const exempt = new Set([handle, ...(config.minQtyExempt || [])]);
  const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
  let cart = await cartResponse.json();
  let rendered = null;

  if (config.minQty) {
    const raises = {};
    (cart.items || []).forEach((item) => {
      if (exempt.has(item.handle) || item.product_type === 'Add-on') return;
      const min = minQtyFor(item.price, config);
      if (item.quantity > 0 && item.quantity < min) raises[item.key] = min;
    });
    if (Object.keys(raises).length) {
      rendered = await postCartUpdate(raises);
      cart = rendered;
      announce('Minimum quantity applied.');
    }
  }

  const items = cart.items || [];
  const feeItems = items.filter((item) => item.handle === handle);
  if (config.enabled || feeItems.length) {
    const map = config.enabled ? await loadPaymentFeeMap(config.map) : null;
    const subtotal = items
      .filter((item) => item.handle !== handle)
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    const wanted = map ? paymentFeeLines(paymentFeeCents(subtotal, config), map.ids) : new Map();
    const current = new Map();
    feeItems.forEach((item) => current.set(item.variant_id, (current.get(item.variant_id) || 0) + item.quantity));
    const updates = {};
    current.forEach((quantity, id) => {
      if ((wanted.get(id) || 0) !== quantity) updates[id] = wanted.get(id) || 0;
    });
    wanted.forEach((quantity, id) => {
      if (current.get(id) !== quantity) updates[id] = quantity;
    });
    if (Object.keys(updates).length) rendered = await postCartUpdate(updates);
  }

  if (rendered) applyPaymentFeeSections(rendered);
};

let paymentFeeRun = null;
let paymentFeeQueued = false;
const syncPaymentFee = () => {
  if (paymentFeeRun) {
    paymentFeeQueued = true;
    return paymentFeeRun;
  }
  paymentFeeRun = (async () => {
    try {
      do {
        paymentFeeQueued = false;
        await reconcilePaymentFee();
      } while (paymentFeeQueued);
    } catch (error) {
      console.warn('[payment-fee]', error);
    } finally {
      paymentFeeRun = null;
    }
  })();
  return paymentFeeRun;
};

// Checkout forms wait for the cart rules before they submit.
const holdCheckoutForPaymentFee = (event, form, submitter) => {
  const config = paymentFeeConfig();
  if (!config?.enabled && !config?.minQty) return false;
  if (form.dataset.paymentFeeChecked === 'true') {
    delete form.dataset.paymentFeeChecked;
    return false;
  }
  event.preventDefault();
  const kind = form.dataset.cartCheckoutForm;
  const button = submitter || form.querySelector('[name="checkout"]');
  setButtonLoading(button, true);
  syncPaymentFee().finally(() => {
    const freshForm = document.querySelector(`[data-cart-checkout-form="${kind}"]`) || form;
    const freshButton = freshForm.querySelector('[name="checkout"]');
    freshForm.dataset.paymentFeeChecked = 'true';
    setButtonLoading(button, false);
    if (freshButton) freshForm.requestSubmit(freshButton);
    else freshForm.submit();
  });
  return true;
};

// Minimum quantity UI: the hint under a quantity box turns into a short message
// when the buyer tries to go below the minimum.
const minQtyText = (template, quantity, unitCents) =>
  String(template || '')
    .replace(/\[qty\]/g, String(quantity))
    .replace(/\[price\]/g, formatMoney(unitCents, paymentFeeConfig()?.moneyFormat));

const minQtyNoteFor = (input) =>
  (input.closest('[data-min-qty-scope]') || input.form)?.querySelector('[data-min-qty-note]');

const showMinQtyError = (input, minimum) => {
  const box = input.closest('.quantity');
  if (box) {
    box.classList.remove('is-min-error');
    void box.offsetWidth; // restart the nudge
    box.classList.add('is-min-error');
  }
  const note = minQtyNoteFor(input);
  if (!note) return;
  note.textContent = minQtyText(
    paymentFeeConfig()?.minQtyError || 'Minimum quantity for this item is [qty].',
    minimum,
    Number(input.dataset.unitPrice) || 0,
  );
  note.hidden = false;
  note.classList.add('is-error');
  note.setAttribute('role', 'alert');
  window.clearTimeout(note.minQtyTimer);
  note.minQtyTimer = window.setTimeout(() => {
    note.classList.remove('is-error');
    note.removeAttribute('role');
    note.textContent = note.dataset.hint || '';
    note.hidden = !note.dataset.hint;
    box?.classList.remove('is-min-error');
  }, 3500);
};

document.addEventListener(
  'change',
  (event) => {
    const input = event.target.closest?.('input[data-min-qty]');
    if (!input) return;
    const minimum = Number(input.dataset.minQty) || 1;
    const value = Math.floor(Number(input.value) || 0);
    const cartLine = input.hasAttribute('data-cart-quantity');
    if (value >= minimum || (cartLine && value === 0)) return;
    event.stopImmediatePropagation();
    input.value = String(cartLine ? Math.max(minimum, Number(input.defaultValue) || minimum) : minimum);
    showMinQtyError(input, minimum);
    if (!cartLine) refreshLivePrice(input.closest('[data-product-section]'));
  },
  true,
);

// Product pages: "−" at the minimum explains why the number does not drop.
document.addEventListener(
  'click',
  (event) => {
    const control = event.target.closest?.('[data-quantity-action="decrease"]');
    const input = control?.parentElement?.querySelector('input[data-min-qty]');
    if (!input || input.hasAttribute('data-cart-quantity')) return;
    const minimum = Number(input.dataset.minQty) || 1;
    if (minimum > 1 && Math.floor(Number(input.value) || 0) <= minimum) showMinQtyError(input, minimum);
  },
  true,
);

// Variant change: the minimum follows the selected variant's price.
document.addEventListener('change', (event) => {
  const select = event.target.closest?.('variant-selects select');
  if (!select) return;
  const selects = select.closest('variant-selects');
  const variant = selects?.variants?.find((item) => String(item.id) === select.value);
  const scope = selects?.closest('[data-product-section]');
  const input = scope?.querySelector('input[name="quantity"][data-min-qty]');
  if (!variant || !input) return;
  const previous = Number(input.dataset.minQty) || 1;
  const minimum = minQtyFor(variant.price);
  input.dataset.minQty = String(minimum);
  input.dataset.unitPrice = String(variant.price);
  input.min = String(minimum);
  const value = Number(input.value) || 0;
  if (value < minimum || value === previous) input.value = String(minimum);
  const note = minQtyNoteFor(input);
  if (note) {
    note.dataset.hint =
      minimum > 1
        ? minQtyText(paymentFeeConfig()?.minQtyHint || 'Minimum quantity: [qty]', minimum, variant.price)
        : '';
    if (!note.classList.contains('is-error')) {
      note.textContent = note.dataset.hint;
      note.hidden = !note.dataset.hint;
    }
  }
  refreshLivePrice(scope);
});

document.querySelectorAll('input[name="quantity"][data-min-qty]').forEach((input) => {
  if (Number(input.dataset.minQty) > 1) refreshLivePrice(input.closest('[data-product-section]'));
});
if (Number(paymentFeeConfig()?.count) > 0) syncPaymentFee();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncPaymentFee();
});
